import { useState } from "react";
import { Mail, Search, Phone } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    Checkbox,
    Combobox,
    DateField,
    DateRangeField,
    FileUpload,
    FormField,
    Input,
    MultiSelect,
    Radio,
    SearchableDropdown,
    SecretField,
    Select,
    Switch,
    TagInput,
    Textarea,
    ViewAs,
    SegmentedControl,
    type ViewAsMode,
} from "@maeiz/ui";
import { DocPage, Section, Demo, SpecTable } from "../../components/DocChrome";

export function FormsPage() {
    const [area, setArea] = useState("dha");
    const [city, setCity] = useState("khi");
    const [status, setStatus] = useState("open");
    const [tags, setTags] = useState<string[]>(["DHA", "VIP"]);
    const [zones, setZones] = useState<(string | number)[]>(["dha"]);
    const [start, setStart] = useState("2026-10-01");
    const [end, setEnd] = useState("2026-10-07");
    const [view, setView] = useState<ViewAsMode>("table");
    const [period, setPeriod] = useState("year");

    return (
        <DocPage
            title="Form Controls"
            description="Custom select & calendars (no native pickers). Multi-select with checkboxes. Secret field. Collision-aware overlays."
        >
            <Section title="Control contract">
                <SpecTable
                    rows={[
                        ["Select", "Radix custom menu — not native <select>"],
                        ["Dates", "Custom Calendar popover"],
                        ["FileUpload", "Drag & drop + preview + size check"],
                        ["MultiSelect", "Checkbox list + search"],
                        ["SecretField", "Show / hide toggle"],
                        ["SegmentedControl", "Generic legend + pills (any label/options)"],
                        ["ViewAs", "Optional preset: Grid · List · Table"],
                    ]}
                />
            </Section>

            <Section title="Text & secrets">
                <Demo className="grid max-w-xl gap-4">
                    <Input
                        label="Search"
                        placeholder="Find customer…"
                        prefix={<Search />}
                        fullWidth
                    />
                    <Input
                        label="Email"
                        type="email"
                        placeholder="ops@maeiz.org"
                        prefix={<Mail />}
                        fullWidth
                    />
                    <Input
                        label="Phone"
                        placeholder="300 1234567"
                        prefixAddon="+92"
                        prefix={<Phone />}
                        fullWidth
                    />
                    <SecretField label="API secret" placeholder="••••••••" fullWidth />
                    <FormField id="notes" label="Notes">
                        <Textarea id="notes" placeholder="Delivery notes…" fullWidth />
                    </FormField>
                </Demo>
            </Section>

            <Section title="File upload">
                <Demo className="max-w-xl space-y-4">
                    <FileUpload
                        label="Company logo or profile photo"
                        helperText="PNG, JPG or GIF up to 5 MB"
                    />
                </Demo>
            </Section>

            <Section title="Selects">
                <Demo className="grid max-w-xl gap-4">
                    <Select
                        label="Simple select (custom)"
                        fullWidth
                        value={status}
                        onValueChange={setStatus}
                        options={[
                            { value: "open", label: "Open" },
                            { value: "closed", label: "Closed" },
                            { value: "hold", label: "On hold" },
                        ]}
                    />
                    <div>
                        <p className="mb-1.5 text-sm font-medium text-neutral-dark dark:text-white">
                            Combobox
                        </p>
                        <Combobox
                            value={area}
                            onChange={setArea}
                            options={[
                                { value: "dha", label: "DHA Phase 5" },
                                { value: "gulshan", label: "Gulshan" },
                                { value: "clifton", label: "Clifton" },
                            ]}
                        />
                    </div>
                    <SearchableDropdown
                        label="City (searchable dropdown)"
                        value={city}
                        onChange={setCity}
                        options={[
                            { value: "khi", label: "Karachi" },
                            { value: "lhr", label: "Lahore" },
                            { value: "isb", label: "Islamabad" },
                        ]}
                    />
                    <MultiSelect
                        label="Zones (multi + search)"
                        fullWidth
                        value={zones}
                        onChange={setZones}
                        options={[
                            { value: "dha", label: "DHA" },
                            { value: "gulshan", label: "Gulshan" },
                            { value: "clifton", label: "Clifton" },
                            { value: "korangi", label: "Korangi" },
                        ]}
                    />
                </Demo>
            </Section>

            <Section title="Tags">
                <Demo className="max-w-xl">
                    <TagInput
                        label="Areas / tags"
                        value={tags}
                        onChange={setTags}
                        suggestions={["DHA", "Gulshan", "Clifton", "VIP", "COD", "Wholesale"]}
                        placeholder="Add tag…"
                    />
                </Demo>
            </Section>

            <Section title="Dates (custom calendar)">
                <Demo className="grid max-w-xl gap-4">
                    <DateField label="Delivery date" required value={start} onChange={setStart} />
                    <DateRangeField
                        label="Report range"
                        startValue={start}
                        endValue={end}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                    />
                </Demo>
            </Section>

            <Section title="Segmented control">
                <Demo className="flex flex-wrap items-end gap-4">
                    <ViewAs value={view} onChange={setView} />
                    <SegmentedControl
                        label="Filtered by"
                        value={period}
                        onChange={setPeriod}
                        options={[
                            { value: "today", label: "Today" },
                            { value: "week", label: "This week" },
                            { value: "month", label: "This month" },
                            { value: "year", label: "This year" },
                            { value: "all", label: "All" },
                        ]}
                    />
                    <Input label="Aligned height" placeholder="h-11 match" className="w-44" />
                </Demo>
            </Section>

            <Section title="Toggles">
                <Demo className="flex flex-wrap items-end gap-4">
                    <Checkbox label="Cash collected" defaultChecked />
                    <Radio name="pay" label="Safepay" value="safepay" defaultChecked />
                    <Switch label="Push notifications" defaultChecked />
                    <Button>Submit</Button>
                    <Accordion type="single" collapsible className="w-full max-w-md">
                        <AccordionItem value="adv">
                            <AccordionTrigger>Advanced</AccordionTrigger>
                            <AccordionContent>Optional overrides.</AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </Demo>
            </Section>
        </DocPage>
    );
}
